import { UserModelRole, TableDetails, TableSourceJS } from "target_wasm";
import { useModelDeletionStatus } from "./useModelDeletionStatus";

export function role_to_string(role: UserModelRole): string {
  switch (role) {
    case UserModelRole.Viewer:
      return "Viewer";
    case UserModelRole.Collaborator:
      return "Collaborator";
    case UserModelRole.Owner:
      return "Owner";
    case UserModelRole.None:
      return "None";
  }
}

export function can_edit_model(
  role: UserModelRole | undefined | null,
): boolean {
  switch (role) {
    case UserModelRole.Collaborator:
    case UserModelRole.Owner:
      return true;
    default:
      return false;
  }
}

export function can_rename_model(
  role: UserModelRole | undefined | null,
): boolean {
  switch (role) {
    case UserModelRole.Owner:
      return true;
    default:
      return false;
  }
}

export function can_import(role: UserModelRole | undefined | null): boolean {
  switch (role) {
    case UserModelRole.Owner:
      return true;
    default:
      return false;
  }
}

// A catalog model has no owner — catalog managers resolve to Collaborator, so
// gate import on can_edit_model there rather than owner-only.
export function can_import_into(
  role: UserModelRole | undefined | null,
  isCatalog: boolean,
): boolean {
  return can_import(role) || (isCatalog && can_edit_model(role));
}

/** Whoever may see a model's shared-with list opens its Manage access, outside
 *  the catalog and the Trash; managing is the dialog's own question. */
export function canOpenModelAccess(
  perms: { can_see_shared_with_list?: boolean; is_catalog?: boolean } | null,
  scheduled: boolean,
): boolean {
  return !scheduled && !!perms?.can_see_shared_with_list && !perms.is_catalog;
}

/** Granting and revoking in that dialog: a stronger right, the same exclusions. */
export function canManageModelAccess(
  perms: { can_manage_access?: boolean; is_catalog?: boolean } | null,
  scheduled: boolean,
): boolean {
  return !scheduled && !!perms?.can_manage_access && !perms.is_catalog;
}

export function can_transfer_ownership(
  role: UserModelRole | undefined | null,
): boolean {
  switch (role) {
    case UserModelRole.Owner:
      return true;
    default:
      return false;
  }
}

// Mirrors the server rule `can_transfer_tables` = `owner or catalog_manager`.
// A catalog model has no owner — its managers arrive as Collaborator — so gate
// on can_edit_model there rather than owner-only.
export function can_transfer_tables(
  role: UserModelRole | undefined | null,
  isCatalog: boolean,
): boolean {
  return can_transfer_ownership(role) || (isCatalog && can_edit_model(role));
}

/** When the details ⋮ offers Manage Access on a table, and so the bar does too. */
export function canShareTable(
  permissions:
    | { can_see_shared_with_list?: boolean; can_share?: boolean }
    | null
    | undefined,
): boolean {
  return !!permissions?.can_see_shared_with_list && !!permissions?.can_share;
}

/** When the details ⋮ offers Transfer Ownership, and so the bar does too. */
export function canTransferTable(
  role: UserModelRole | undefined | null,
  isCatalog: boolean,
  permissions: { can_transfer?: boolean } | null | undefined,
): boolean {
  return can_transfer_tables(role, isCatalog) && !!permissions?.can_transfer;
}

interface TrashPermissions {
  is_catalog?: boolean;
  can_trash_tables?: boolean;
}

// On a catalog model the editing role is catalog-manage, and only a publisher
// removes a table — the server's `can_trash_table`, sent with the permissions.
export function canTrashTables(
  permissions: TrashPermissions | null | undefined,
): boolean {
  return !permissions?.is_catalog || !!permissions.can_trash_tables;
}

// The actor asks `can_trash_table` of what reaches the Trash or is purged; an
// un-import needs only the editing right.
export function canDeleteTable(
  source: TableSourceJS,
  permissions: TrashPermissions | null | undefined,
): boolean {
  return isImportedSource(source) || canTrashTables(permissions);
}

// Mirrors the server rule: FGA `can_hard_delete` = `owner or catalog_publisher`
// on the parent model, plus a `created_by` carve-out FGA can't express, which a
// catalog model does not honour.
export function canHardDelete(
  table: TableDetails,
  user_id: string,
  role: UserModelRole,
  permissions: TrashPermissions | null | undefined,
): boolean {
  if (permissions?.is_catalog) {
    return canTrashTables(permissions);
  }

  if (role === UserModelRole.Owner) {
    return true;
  }

  if (role === UserModelRole.Collaborator && table.created_by === user_id) {
    return true;
  }

  return false;
}

export function canEditTableHistory(
  table: TableDetails,
  role: UserModelRole,
): boolean {
  if (isImportedSource(table.source)) {
    return false;
  }
  if (role === UserModelRole.Owner || role === UserModelRole.Collaborator) {
    return true;
  }

  return false;
}

export function isTableOwned(source: TableSourceJS): boolean {
  return (
    source === TableSourceJS.Owned ||
    source === TableSourceJS.GoogleSheet ||
    source === TableSourceJS.Postgres ||
    source === TableSourceJS.Supabase ||
    source === TableSourceJS.LocalSqliteDatabase
  );
}

export function canEditTableSchema(source: TableSourceJS): boolean {
  // After the FileUpload retirement, uploaded tables are tagged Owned —
  // this predicate now collapses to a single check.
  return source === TableSourceJS.Owned;
}

/// A recipe's materialized result (docs/Recipes.md): editable by nobody, its
/// recipe overwrites it in place. Informational, like `isConnectorSource` —
/// the canvas's own gates already refuse every edit on it.
export function isRecipeResultSource(source: TableSourceJS): boolean {
  return source === TableSourceJS.RecipeResult;
}

/// Pure-structural test: is this `TableSourceJS` a connector-driven
/// source (Postgres / Supabase / Bucket / LocalSqliteDatabase /
/// GoogleSheet)? Useful for INFORMATIONAL UI labeling — e.g. rendering a
/// "Parent-locked: external source" badge.
///
/// DO NOT use for affordance gating. The server-derived
/// `TablePermissions.can_X` booleans (fetched via
/// `clientApp.fetch_table_permissions`) are the authoritative source for
/// share / import / pin / checkpoint / transfer affordances. Connector
/// tables share, import, checkpoint and transfer like Owned tables; the
/// parent-lock only keeps them out of the catalog, and WHERE one may move
/// (inside its connection's org) is the destination picker's answer.
export function isConnectorSource(source: TableSourceJS): boolean {
  return (
    source === TableSourceJS.Postgres ||
    source === TableSourceJS.Supabase ||
    source === TableSourceJS.Bucket ||
    source === TableSourceJS.LocalSqliteDatabase ||
    source === TableSourceJS.GoogleSheet
  );
}

/// **Deprecated** — synchronous affordance gate, kept as a thin alias.
/// Replaced by server-hydrated `TablePermissions.can_share` /
/// `can_import` / `can_checkpoint` / `can_transfer` booleans (fetched
/// via `clientApp.fetch_table_permissions`). For purely informational
/// labeling (e.g. "this is a connector source" badges), use
/// [`isConnectorSource`] — same predicate, intent-named.
///
/// Kept as a thin alias so any straggler caller still compiles. New
/// code MUST NOT use this name; ESLint should flag it as a follow-up.
export function isTableLockedToParent(source: TableSourceJS): boolean {
  return (
    source === TableSourceJS.Postgres ||
    source === TableSourceJS.Supabase ||
    source === TableSourceJS.Bucket ||
    source === TableSourceJS.LocalSqliteDatabase ||
    source === TableSourceJS.GoogleSheet
  );
}

export const SEALED_TOOLTIP =
  "This table is in a sealed space — its data cannot leave. It can be viewed by people with access to the space, but not imported, transferred, or exported.";

export const PARENT_LOCKED_TOOLTIP =
  "External-source tables can be shared, imported and checkpointed like any table, but they stay with the credentials that connected them: they cannot be transferred or published to the catalog.";

export function isTerminalImportedState(source: TableSourceJS): boolean {
  return (
    source === TableSourceJS.ImportedTombstoned ||
    source === TableSourceJS.ImportedAccessLost
  );
}

/// The coarse gate on adding a link: a model-editor act, not a schema edit. A
/// recipe result's links are derived, never drawn (R23), so the server refuses
/// any touching one. The pair rule is the dialog's and the server's.
export const canEditTableLinks = (
  canEditModel: boolean,
  source: TableSourceJS | undefined,
): boolean =>
  canEditModel &&
  source !== undefined &&
  !isTerminalImportedState(source) &&
  !isRecipeResultSource(source);

export function isTerminalImported(source: TableSourceJS): boolean {
  return isTerminalImportedState(source);
}

/// Any imported-table lifecycle state (active, inactive, waiting-for-approval,
/// pending-release, or terminal). Importers can only restore released
/// versions, so checkpoint history surfaces releases only for these sources.
export function isImportedSource(source: TableSourceJS): boolean {
  return (
    source === TableSourceJS.Imported ||
    source === TableSourceJS.ImportedInactive ||
    source === TableSourceJS.ImportedWaitingForApproval ||
    source === TableSourceJS.ImportedPendingRelease ||
    isTerminalImportedState(source)
  );
}

export function useCanEditModelInCanvas(
  role: UserModelRole | undefined | null,
): boolean {
  const isModelScheduled = useModelDeletionStatus();
  return can_edit_model(role) && !isModelScheduled;
}
